import { useMemo, type FC, memo } from "react"
import { useNavigate, useNavigation } from "@remix-run/react"
import { useQuery } from "@apollo/client/index.js"
import { ANALYTICS_EVENTS } from "~/helpers/analyticsEvents"

import type { CapsuleProps } from "~/components/capsules"
import Layout from "~/components/capsules/Layout"
import type { WithCapsuleActivityTypeFragment } from "~/contentful"
import { useConfigContext } from "~/providers/ConfigProvider"
import ProductHero, {
  ProductHeroSkeleton
} from "~/components/design-system/organisms/ProductHero"
import type { GalleryItem } from "~/components/design-system/molecules/MediaGalleryWithThumbnails"
import { activityTypeImagesToGalleryItems } from "~/helpers/activityTypeMedia"
import { Button } from "~/components/design-system/atoms/Button"
import {
  CachedActivityTypeProductHeroDocument,
  ActivityTypeBestPriceDocument,
  LocationBySlugDocument,
  ActivityTypeUrgencyBannerTypeEnum
} from "~/graphql"
import { activityBookingPath } from "~/helpers/routes"
import { formatCurrencyWithoutTrailingZeros } from "~/helpers/format"
import { ProductMetadataRow } from "~/components/design-system/organisms/ProductHero/ProductMetadataRow"
import ViewerUrgencyBanner from "~/components/activity-type-form/ViewerUrgencyBanner"
import {
  activityTypeBulletsForProductHero,
  getActivityTypeDescription
} from "~/helpers/activityTypeBullets"
import {
  resolveLocationRouteSlug,
  resolveLocationSlugAlias
} from "~/helpers/location"
import { useAnalytics } from "~/providers/AnalyticsProvider"

interface Props extends CapsuleProps {
  capsule: WithCapsuleActivityTypeFragment
  activityPriceMap?: Record<string, number | null>
}

const ActivityType: FC<Props> = memo(
  ({
    page,
    capsule,
    customer,
    membershipProduct,
    topBunkMembershipDiscount,
    activityPriceMap
  }) => {
    const config = useConfigContext()
    const navigate = useNavigate()
    const navigation = useNavigation()
    const { track } = useAnalytics()
    const isCTALoading =
      navigation.state === "loading" &&
      (navigation.location?.pathname.includes("/booking") ?? false)

    const activityTypeId = useMemo(
      () => config.stubActivityTypeId ?? capsule.activityType.activityTypeId,
      [config.stubActivityTypeId, capsule.activityType.activityTypeId]
    )

    const routeLocationSlug = capsule.location ?? undefined
    const locationSlug = routeLocationSlug
      ? resolveLocationSlugAlias(routeLocationSlug)
      : undefined
    const bookingLocationSlug = locationSlug
      ? resolveLocationRouteSlug(locationSlug)
      : undefined

    const { data, loading, error } = useQuery(
      CachedActivityTypeProductHeroDocument,
      {
        variables: { id: activityTypeId }
      }
    )

    if (error) console.error("[ActivityType capsule] query error:", error)

    const activityType = data?.activityType

    const { data: locationData } = useQuery(LocationBySlugDocument, {
      variables: { slug: locationSlug! },
      skip: !locationSlug
    })

    const { data: routeLocationData } = useQuery(LocationBySlugDocument, {
      variables: { slug: routeLocationSlug! },
      skip:
        !routeLocationSlug ||
        routeLocationSlug === locationSlug ||
        Boolean(locationData?.locationBySlug)
    })

    const resolvedLocation =
      locationData?.locationBySlug ?? routeLocationData?.locationBySlug

    const locationId = resolvedLocation?.id ?? undefined

    // _page.$.tsx batches prices server-side; other routes (locations,
    // products, ...) pass no map, so fall back to the per-capsule query.
    const { data: bestPriceData } = useQuery(ActivityTypeBestPriceDocument, {
      variables: { id: activityTypeId, locationId: locationId! },
      skip: Boolean(activityPriceMap) || !locationId
    })

    const bestPriceInCents =
      bestPriceData?.activityType?.bestPrice ??
      bestPriceData?.activityType?.availableActivities?.[0]
        ?.signedPriceChildTierCurrent?.price
    const bestPrice = activityPriceMap
      ? locationSlug
        ? (activityPriceMap[`${activityTypeId}:${locationSlug}`] ?? null)
        : null
      : bestPriceInCents != null
        ? bestPriceInCents / 100
        : null

    const ctaButton = useMemo(() => {
      if (!activityType) return undefined

      const handleClick = () => {
        track(ANALYTICS_EVENTS.BOOKING_GET_TICKETS)
        if (bookingLocationSlug) {
          navigate(
            activityBookingPath(
              bookingLocationSlug,
              activityType.slug,
              "schedule"
            )
          )
        } else {
          navigate(`/activities/${activityType.slug}`)
        }
      }

      return (
        <Button variant="primary" size="xlarge" full onClick={handleClick}>
          <span className="text-[20px] md:text-2xl">Get Tickets</span>
          {bestPrice != null && (
            <span className="text-[16px] md:text-[18px] ml-1 font-light">
              (from {formatCurrencyWithoutTrailingZeros(bestPrice)})
            </span>
          )}
        </Button>
      )
    }, [activityType, bookingLocationSlug, navigate, bestPrice, track])

    // Key `media` on the fields it uses so the memoized gallery (and carousel)
    // is not re-initialized when unrelated fields of `activityType` change.
    const activityImages = activityType?.images
    const activityName = activityType?.name
    const media: GalleryItem[] = useMemo(
      () =>
        activityImages
          ? activityTypeImagesToGalleryItems(activityImages, activityName ?? "")
          : [],
      [activityImages, activityName]
    )

    const bulletsForLocation = useMemo(
      () =>
        activityType
          ? activityTypeBulletsForProductHero(
              activityType.bullets,
              locationId ?? null
            )
          : [],
      [activityType, locationId]
    )

    return (
      <>
        <div className="mt-12"></div>
        <Layout>
          {loading ? (
            <ProductHeroSkeleton />
          ) : activityType ? (
            <ProductHero
              media={media}
              title={activityType.name}
              starRating={{ rating: 5, text: "Loved by thousands of families" }}
              subtitle={activityType.tagline ?? undefined}
              locationName={resolvedLocation?.name}
              reserveLocationSpace={Boolean(locationSlug)}
              description={getActivityTypeDescription(
                bulletsForLocation[0]?.value
              )}
              accordionItems={bulletsForLocation.slice(1)}
              metadataTags={
                activityType.tags.length > 0 ? (
                  <ProductMetadataRow tags={activityType.tags} />
                ) : undefined
              }
              urgencyIndicator={
                activityType.urgencyBannerType !==
                  ActivityTypeUrgencyBannerTypeEnum.HIDDEN && (
                  <ViewerUrgencyBanner
                    numberOfViewers={activityType.viewershipEstimate || 50}
                    bannerType={activityType.urgencyBannerType}
                    bannerText={activityType.urgencyBannerText}
                  />
                )
              }
              ctaButton={ctaButton}
              ctaFixedBar={true}
              ctaLoading={isCTALoading}
            />
          ) : null}
        </Layout>
      </>
    )
  }
)

ActivityType.displayName = "ActivityType"

export default ActivityType
